// packages/shared/src/services/cart.service.ts
import {
  doc,
  updateDoc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { COLLECTIONS } from '@bookstore/shared';
import { getFirebaseFirestore } from '@bookstore/shared/browser';


export interface CartItemInput {
  productId: string;
  quantity: number;
}

export class CartService {
  private get db() {
    return getFirebaseFirestore();
  }

  /**
   * Get user's cart items (IDs + quantities only)
   */
  async getCart(userId: string): Promise<CartItemInput[]> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return [];
      }

      return userDoc.data()?.cart || [];
    } catch (error) {
      console.error('Error getting cart:', error);
      throw new Error('Failed to get cart');
    }
  }

  /**
   * Add item to cart or update quantity
   */
  async addToCart(userId: string, productId: string, quantity: number = 1): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const cart = userDoc.data()?.cart || [];
      const existingItemIndex = cart.findIndex((item: any) => item.productId === productId);

      let updatedCart;
      if (existingItemIndex !== -1) {
        // Update quantity of existing item
        updatedCart = [...cart];
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          quantity: updatedCart[existingItemIndex].quantity + quantity,
        };
      } else {
        // Add new item
        updatedCart = [
          ...cart,
          {
            productId,
            quantity,
            addedAt: new Date(),
          },
        ];
      }

      await updateDoc(userRef, {
        cart: updatedCart,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw new Error('Failed to add to cart');
    }
  }

  /**
   * Update item quantity
   */
  async updateQuantity(userId: string, productId: string, quantity: number): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const cart = userDoc.data()?.cart || [];

      if (quantity <= 0) {
        // Remove item if quantity is 0
        const updatedCart = cart.filter((item: any) => item.productId !== productId);
        await updateDoc(userRef, {
          cart: updatedCart,
          updatedAt: serverTimestamp(),
        });
        return;
      }

      const updatedCart = cart.map((item: any) =>
        item.productId === productId ? { ...item, quantity } : item
      );

      await updateDoc(userRef, {
        cart: updatedCart,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw new Error('Failed to update quantity');
    }
  }

  /**
   * Remove item from cart
   */
  async removeFromCart(userId: string, productId: string): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const cart = userDoc.data()?.cart || [];
      const updatedCart = cart.filter((item: any) => item.productId !== productId);

      await updateDoc(userRef, {
        cart: updatedCart,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw new Error('Failed to remove from cart');
    }
  }

  /**
   * Clear entire cart
   */
  async clearCart(userId: string): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, {
        cart: [],
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw new Error('Failed to clear cart');
    }
  }
}

export const cartService = new CartService();